import { useMemo, useState } from 'react'
import { fbt } from 'fbtee'
import { QrCode, Check, UserPlus, Share2, Link } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useFriendships } from '@/hooks/useFriendships'
import { useUserProfiles } from '@/hooks/useUserProfiles'
import { unknownMemberName } from '@/lib/commonStrings'
import { getInviteUrl, getInviteMessage, compareByDisplayName } from '@/lib/utils'
import { copyInviteLink, nativeShareInvite, canNativeShare } from '@/lib/share'
import { UserRow } from '@/components/UserRow'
import { InviteQrDialog } from '@/components/InviteQrDialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Group, GroupInvite } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: Group
  invites: GroupInvite[]
  onSendInvite: (toUserId: string, fromUserId: string, group: Group) => Promise<void>
}

export function InviteMembersDialog({
  open,
  onOpenChange,
  group,
  invites,
  onSendInvite,
}: Props) {
  const { user } = useAuth()
  const userId = user?.uid ?? null
  const { friendIds } = useFriendships(userId)
  const friendProfiles = useUserProfiles(friendIds)
  const [showQr, setShowQr] = useState(false)

  const memberSet = useMemo(() => new Set(group.memberIds), [group.memberIds])
  const invitedSet = useMemo(
    () => new Set(invites.map(i => i.toUserId)),
    [invites],
  )

  const sortedFriends = useMemo(
    () =>
      [...friendIds].sort((a, b) =>
        compareByDisplayName(a, b, friendProfiles),
      ),
    [friendIds, friendProfiles],
  )

  const inviteUrl = getInviteMessage(group.inviteCode)

  async function handleCopy() {
    await copyInviteLink(inviteUrl, 'group_invite_dialog')
  }

  async function handleShare() {
    await nativeShareInvite(inviteUrl, 'group_invite_dialog')
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {fbt(
                'Invite to ' + fbt.param('groupName', group.name),
                'Title of the invite-members dialog',
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {fbt(
                'Share an invite link or invite friends directly.',
                'Accessible description for the invite-members dialog',
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-9 cursor-pointer"
              onClick={handleCopy}
            >
              <Link className="size-3.5" />
              {fbt('Link', 'Button to copy group invite link in the invite-members dialog')}
            </Button>
            {canNativeShare() && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-9 cursor-pointer"
                onClick={handleShare}
              >
                <Share2 className="size-3.5" />
                {fbt('Share', 'Button to open native share sheet for group invite link')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-9 cursor-pointer"
              onClick={() => {
                onOpenChange(false)
                setShowQr(true)
              }}
            >
              <QrCode className="size-3.5" />
              {fbt('QR', 'Button to show QR code in the invite-members dialog')}
            </Button>
          </div>

          {sortedFriends.length > 0 && (
            <div className="flex flex-col gap-2 overflow-y-auto -mx-6 px-6 pt-1">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                {fbt('Friends', 'Section heading in the invite-members dialog showing the friend list')}
              </span>
              <ul className="flex flex-col gap-2">
                {sortedFriends.map(fid => {
                  const profile = friendProfiles[fid]
                  const name = profile?.displayName?.trim() || unknownMemberName()
                  const isMember = memberSet.has(fid)
                  const isInvited = invitedSet.has(fid)

                  let action
                  if (isMember) {
                    action = (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Check className="size-3.5 text-primary" />
                        {fbt('Member', 'Badge shown next to a friend who is already a group member in the invite dialog')}
                      </span>
                    )
                  } else if (isInvited) {
                    action = (
                      <span className="text-xs text-muted-foreground">
                        {fbt('Invited', 'Badge shown next to a friend who has already been invited to the group')}
                      </span>
                    )
                  } else {
                    action = (
                      <button
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-pointer"
                        onClick={() => userId && onSendInvite(fid, userId, group)}
                      >
                        <UserPlus className="size-3.5 text-primary" />
                        {fbt('Invite', 'Button to send a group invite to a friend')}
                      </button>
                    )
                  }

                  return (
                    <UserRow
                      key={fid}
                      name={name}
                      profile={profile}
                      action={action}
                    />
                  )
                })}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <InviteQrDialog
        open={showQr}
        onOpenChange={setShowQr}
        value={getInviteUrl(group.inviteCode)}
        title={fbt(
          'Invite to ' + fbt.param('groupName', group.name),
          'Title of the group invite QR code dialog',
        )}
        description={fbt(
          'Scan this code or share the link to join the group.',
          'Instructions in the group invite QR code dialog',
        )}
      />
    </>
  )
}
